import { useState } from 'react';

import styled from 'styled-components';

import { productSwitchesUpdatedOrCreatedHandler } from '../cacheHandlers/productSwitchesCacheHandler';

import { TotemDatePicker } from 'components/TotemDatePicker';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { ProductSelector } from '../ProductSelector';

import { TotemInput } from 'components/TotemInput';
import { TotemSwitch } from 'components/TotemSwitch';
import { GetAllProductsQuery, ProductSwitchCreateInput, useCreateProductSwitchMutation } from 'data/__generated__';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';

type ProductSwitchCreateType = Omit<ProductSwitchCreateInput, 'endDate' | 'productAId' | 'productBId' | 'startDate'> & {
    endDate: null | string;
    productAId: null | string;
    productBId: null | string;
    startDate: null | string;
};

export function CreateProductSwitchPopup({
    isOpen,
    setIsOpen,
    products,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    products: GetAllProductsQuery['getAllProducts'];
}) {
    const [productSwitchForm, setProductSwitchForm] = useState<ProductSwitchCreateType>({
        endDate: null,
        productAId: null,
        productBId: null,
        ratioOfAToOrderForB: 1,
        ratioToOrderOfA: 0,
        startDate: null,
        switchFreefood: false,
        switchStore: true,
    });

    const [createProductSwitch, { loading: creationLoading }] = useCreateProductSwitchMutation();

    async function handleSubmit() {
        const {
            endDate,
            productAId,
            productBId,
            ratioOfAToOrderForB,
            ratioToOrderOfA,
            startDate,
            switchFreefood,
            switchStore,
        } = productSwitchForm;
        if (!productAId || !productBId || !startDate || !endDate) {
            return;
        }
        const { data } = await createProductSwitch({
            variables: {
                productSwitchCreateInput: {
                    endDate,
                    productAId,
                    productBId,
                    ratioOfAToOrderForB,
                    ratioToOrderOfA,
                    startDate,
                    switchFreefood,
                    switchStore,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la création du switch de produit');
        }

        const { createProductSwitch: newProductSwitch } = data;

        productSwitchesUpdatedOrCreatedHandler([newProductSwitch]);

        setIsOpen(false);
    }

    return (
        <TotemPopup
            title="Ajouter un switch de produit"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            contentOverflow="visible"
        >
            <SubmitContainer>
                <ProductSelector
                    label="Produit A"
                    onProductSelection={(productAId) => setProductSwitchForm({ ...productSwitchForm, productAId })}
                    productSelectedId={productSwitchForm.productAId}
                    products={products}
                />
                <ProductSelector
                    label="Produit B"
                    onProductSelection={(productBId) => setProductSwitchForm({ ...productSwitchForm, productBId })}
                    productSelectedId={productSwitchForm.productBId}
                    products={products}
                />
                <TotemDatePicker
                    label="Date de début"
                    selected={productSwitchForm.startDate ? dateFromString(productSwitchForm.startDate, true) : null}
                    onChange={(startDate) => {
                        setProductSwitchForm({
                            ...productSwitchForm,
                            startDate: startDate
                                ? formatDateAsAnniversary({ dateTime: startDate, useNewFormat: true })
                                : null,
                        });
                    }}
                />
                <TotemDatePicker
                    label="Date de fin"
                    selected={productSwitchForm.endDate ? dateFromString(productSwitchForm.endDate, true) : null}
                    onChange={(endDate) => {
                        setProductSwitchForm({
                            ...productSwitchForm,
                            endDate: endDate
                                ? formatDateAsAnniversary({ dateTime: endDate, useNewFormat: true })
                                : null,
                        });
                    }}
                />
                <TotemSwitch
                    label="Changement des variations freefood"
                    checked={productSwitchForm.switchFreefood}
                    onChange={() => {
                        setProductSwitchForm({
                            ...productSwitchForm,
                            switchFreefood: !productSwitchForm.switchFreefood,
                        });
                    }}
                />
                <TotemSwitch
                    label="Changement des variations store"
                    checked={productSwitchForm.switchStore}
                    onChange={() => {
                        setProductSwitchForm({
                            ...productSwitchForm,
                            switchStore: !productSwitchForm.switchStore,
                        });
                    }}
                />
                <TotemInput
                    label="Ratio de commande du produit A"
                    sublabel="0.1 = on commande 10% de ce qu'on aurait du commander."
                    type="number"
                    min="0"
                    step="0.01"
                    value={productSwitchForm.ratioToOrderOfA}
                    onChange={(value) =>
                        setProductSwitchForm({ ...productSwitchForm, ratioToOrderOfA: parseFloat(value) })
                    }
                />
                <TotemInput
                    label="Ratio de commande du produit B par rapport au produit A"
                    sublabel="0.1 = on commande 10% de B de ce qu'on aurait du commander pour A"
                    type="number"
                    min="0"
                    step="0.01"
                    value={productSwitchForm.ratioOfAToOrderForB}
                    onChange={(value) =>
                        setProductSwitchForm({ ...productSwitchForm, ratioOfAToOrderForB: parseFloat(value) })
                    }
                />
                <ValidationButton
                    onClick={handleSubmit}
                    disabled={
                        !productSwitchForm.endDate ||
                        !productSwitchForm.startDate ||
                        !productSwitchForm.productAId ||
                        !productSwitchForm.productBId
                    }
                    loading={creationLoading}
                    useIcon={false}
                    label="Confirmer"
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;
`;

const ValidationButton = styled(TotemPrimaryLoadingButton)`
    margin-top: 15px;
`;
